import { createSlice } from '@reduxjs/toolkit';
import { Service } from 'services/Services';
import { RequestApi } from 'RequestApi';
import { NotificationMessage } from 'components/notification-message/NotificationMessage';
import { logout } from 'auth/authSlice';

const initialState = {
  documents: [],
  documentRecord: null,
  currentPage: 1,
  lastPage: 1,
  loading: false,
  showSettings: { folder: 'New', isClicked: false },
  selectedItems: [],
  viewingDocument: null,
  pageInfo: null,
  fetching: false,
  showRemarkApproval: false,
  progressingDocument: false,
  showPrintOption: false,
  stepTypes: null,
  requester: null,
  stepName: null,
  toggleWorkflow: false,
  toggleWorkflowDecision: false,
  validationErrors: '',
};

const dynamicPageSlice = createSlice({
  name: 'dynamicPage',
  initialState,
  reducers: {
    receiveService(state, action) {
      const { documents, currentPage, lastPage, loading } = action.payload;
      state.documents = documents;
      state.currentPage = currentPage;
      state.lastPage = lastPage;
      state.loading = loading;
    },

    setLoading(state, action) {
      state.loading = action.payload;
    },
    setToggleWorkflow(state, action) {
      state.toggleWorkflow = action.payload;
    },

    setToggleWorkflowDecision(state, action) {
      state.toggleWorkflowDecision = action.payload;
    },

    setFetching(state, action) {
      state.fetching = action.payload;
    },

    setStepTypes(state, action) {
      state.stepTypes = action.payload;
    },

    setRequester(state, action) {
      state.requester = action.payload;
    },

    setStepName(state, action) {
      state.stepName = action.payload;
    },

    toggleSelectDocument(state, action) {
      const item = action.payload;
      if (state.selectedItems.find((x) => x.id === item.id)) {
        state.selectedItems = state.selectedItems.filter((x) => x.id !== item.id);
      } else {
        state.selectedItems = [item, ...state.selectedItems];
      }
    },

    setSelectedDocuments(state, action) {
      state.selectedItems = action.payload;
    },

    setViewingDocument(state, action) {
      state.viewingDocument = action.payload;
    },
    setDocumentRecord(state, action) {
      state.documentRecord = action.payload;
    },

    setShowSettings(state, action) {
      state.showSettings = action.payload;
    },

    setPageInfo(state, action) {
      state.viewingDocument = null;
      state.documents = [];
      state.lastPage = 1;
      state.pageInfo = action.payload;
    },

    setDeleteDocument(state) {
      const index = state.documents.findIndex((element) => element.id.toString() === state.viewingDocument.id.toString());
      if (index !== -1) {
        state.documents.splice(index, 1);
        state.viewingDocument = null;
      }
    },

    updateDocument(state, action) {
      const document = action.payload;

      const index = state.documents.findIndex((element) => element.id.toString() === document.id.toString());

      if (index !== -1) {
        const fieldNames = Object.keys(state.documents[index]);
        const tmpRecord = { ...state.record };
        fieldNames.forEach((fieldName) => {
          tmpRecord[fieldName] = document[fieldName];
        });
        state.documents[index] = tmpRecord;
        state.viewingDocument = tmpRecord;
      }
    },

    setShowRemarkApproval(state, action) {
      state.showRemarkApproval = action.payload;
    },

    setProgressingDocument(state, action) {
      state.progressingDocument = action.payload;
    },
    setValidationErrors(state, action) {
      state.validationErrors = action.payload;
    },

    storeRecord(state, action) {
      const { document } = action.payload;

      const index = state.documents.findIndex((element) => element.id.toString() === document.id.toString());

      if (index !== -1) {
        const arrDocuments = [...state.documents];
        arrDocuments[index] = document;
        state.documents = arrDocuments;
        state.viewingDocument = document;
      } else {
        const arrDocuments = [...state.documents];
        arrDocuments.push(document);
        state.documents = arrDocuments;
        state.viewingDocument = document;
      }
    },

    setShowPrintOption(state, action) {
      state.showPrintOption = action.payload;
    },
  },
});

const { receiveService, setPageInfo, setFetching, storeRecord, setStepTypes, setRequester, setStepName, setValidationErrors } = dynamicPageSlice.actions;

export const {
  toggleSelectDocument,
  setSelectedDocuments,
  setViewingDocument,
  setShowSettings,
  setDeleteDocument,
  setShowRemarkApproval,
  setProgressingDocument,
  updateDocument,
  setShowPrintOption,
  setShowAttachment,
  setToggleWorkflow,
  setToggleWorkflowDecision,
  setDocumentRecord,
} = dynamicPageSlice.actions;

// old code for dynamic
export const createDocument =
  ({ url }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `${url}/store-record?`;
    urlRequest += `auth_token=${Service.userToken}`;

    RequestApi(urlRequest, {})
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          dispatch(storeRecord({ document: res.data.record }));
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
          if (res.data.session_status === 'closed') {
            dispatch(logout());
          }
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

export const deleteDocument =
  ({ url, document }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `${url}/delete-record?`;
    urlRequest += `auth_token=${Service.userToken}`;

    const data = JSON.stringify({
      unid: document.id,
    });

    RequestApi(urlRequest, { data })
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          dispatch(setDeleteDocument());
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

// export const addSubRecord =
//   ({ url, document }) =>
//   async (dispatch) => {
//     dispatch(setFetching(true));

//     let urlRequest = `${url}/add-sub-record?`;
//     urlRequest += `auth_token=${Service.userToken}`;

//     const data = JSON.stringify({
//       unid: document.id,
//     });

//     RequestApi(urlRequest, { data })
//       .then((res) => {
//         dispatch(setFetching(false));
//         if (res.data.status === 'success') {
//           dispatch(setDeleteDocument());
//         } else {
//           NotificationMessage({ message: res.data.msg, status: res.data.status });
//         }
//       })
//       .catch((err) => {
//         dispatch(setFetching(false));
//         console.log(err);
//         NotificationMessage({ message: err.toString(), status: 'error' });
//       });
//   };

export const updateStatusDocument =
  ({ document, status, objectId }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `/approval-workflow-activity/update-status?`;
    urlRequest += `auth_token=${Service.userToken}`;
    urlRequest += `&source_type=portal`;

    const data = JSON.stringify({
      unid: document.id,
      object_id: objectId,
      status,
    });

    RequestApi(urlRequest, { data })
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          dispatch(updateDocument(res.data.record));
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

// --------- end --------  //

export const initPageInfo =
  ({ informationPage }) =>
  async (dispatch) => {
    dispatch(setPageInfo(informationPage));
  };

export const fetchDocument =
  ({ url, folder, searchText = '', page = 1, startingDate = '', endingDate = '' }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `${url}/index-table?`;
    urlRequest += `auth_token=${Service.userToken}`;

    if (page !== 1) {
      urlRequest += `&page=${page}`;
    }

    const data = JSON.stringify({
      flag: folder,
      search_text_value: searchText,
      search_starting_date: startingDate,
      search_ending_date: endingDate,
    });

    RequestApi(urlRequest, {
      data,
    })
      .then((res) => {
        if (res.data.status === 'success') {
          const documents = res.data.records.data;
          const currentPage = res.data.records.current_page;
          const lastPage = res.data.records.last_page;
          dispatch(receiveService({ documents, currentPage, lastPage, loading: false }));
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
          if (res.data.session_status === 'closed') {
            dispatch(logout());
          }
        }
        dispatch(setFetching(false));
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

export const sendApproval =
  ({ objectId, unid }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `/approval-workflow-activity/send-approval?`;
    urlRequest += `auth_token=${Service.userToken}`;
    urlRequest += '&source_type=portal';

    const data = JSON.stringify({
      unid,
      object_id: objectId,
      status: 'Open',
    });

    RequestApi(urlRequest, { data })
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          dispatch(setShowRemarkApproval(true));
          dispatch(setRequester(res.data.data.requester_user_setup));
          dispatch(setStepTypes(res.data.data.step_types));
          dispatch(setStepName(res.data.data.step_names));
          dispatch(setDocumentRecord(res.data.data.record));
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

export const submitSendApproval =
  ({ objectId, unid, mailContent, record }) =>
  async (dispatch, getState) => {
    const state = getState();
    let urlRequest = '';
    let data = {};

    const BreakException = {};
    const errors = {};

    try {
      dispatch(setFetching(true));
      if (state.dynamicPage.pageInfo.url === '/employee-satisfaction-form') {
        urlRequest = `${state.dynamicPage.pageInfo.url}/submit-record?`;
        urlRequest += `auth_token=${Service.userToken}`;

        if (Object.keys(errors).length > 0) {
          dispatch(setFetching(false));
          dispatch(setValidationErrors(errors));
          NotificationMessage({ message: 'Please fill out all required questions', status: 'warning' });
          throw BreakException;
        }

        Object.keys(record.employee_training_questionnaire_lines_groups).forEach((keyValue) => {
          record.employee_training_questionnaire_lines_groups[keyValue].forEach((questionaire, index) => {
            const questionKey = `${keyValue}-${index}`;
            if (questionaire.mandatory === 'Yes' && !questionaire.answer) {
              errors[questionKey] = true;
            }
          });
        });

        if (Object.keys(errors).length > 0) {
          dispatch(setFetching(false));
          dispatch(setValidationErrors(errors));
          NotificationMessage({
            message: 'Please fill out all required questions',
            status: 'warning',
          });
          return;
        }
        // If no errors
        dispatch(setValidationErrors({})); // Clear previous errors

        data = JSON.stringify({
          record,
        });
      } else if (state.dynamicPage.pageInfo.url === '/employee-engagement-survey') {
        urlRequest = `${state.dynamicPage.pageInfo.url}/submit-record?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';

        if (Object.keys(errors).length > 0) {
          dispatch(setFetching(false));
          dispatch(setValidationErrors(errors));
          NotificationMessage({ message: 'Please fill out all required questions', status: 'warning' });
          throw BreakException;
        }

        Object.keys(record.employee_training_questionnaire_lines_groups).forEach((keyValue) => {
          record.employee_training_questionnaire_lines_groups[keyValue].forEach((questionaire, index) => {
            const questionKey = `${keyValue}-${index}`;
            if (questionaire.mandatory === 'Yes' && !questionaire.answer) {
              errors[questionKey] = true;
            }
          });
        });

        if (Object.keys(errors).length > 0) {
          dispatch(setFetching(false));
          dispatch(setValidationErrors(errors));
          NotificationMessage({
            message: 'Please fill out all required questions',
            status: 'warning',
          });
          return;
        }
        // If no errors
        dispatch(setValidationErrors({})); // Clear previous errors

        data = JSON.stringify({
          field_name: 'status',
          value: 'Released',
          unid,
        });
      } else {
        // Default url submitSendApproval
        urlRequest = `/approval-workflow-activity/submit-send-approval?`;
        urlRequest += `auth_token=${Service.userToken}`;
        urlRequest += '&source_type=portal';
        data = JSON.stringify({
          unid,
          object_id: objectId,
          status: 'Open',
          mail_content: mailContent,
        });
      }

      RequestApi(urlRequest, { data })
        .then((res) => {
          dispatch(setFetching(false));
          if (res.data.status === 'success') {
            dispatch(updateDocument(res.data.record));
          } else {
            NotificationMessage({ message: res.data.msg, status: res.data.status });
          }
        })
        .catch((err) => {
          dispatch(setFetching(false));
          console.log(err);
          NotificationMessage({ message: err.toString(), status: 'error' });
        });
    } catch (error) {
      console.log('Error', error);
      dispatch(setFetching(true));
    }
  };

export const submitSendToSuccessor =
  ({ url, unid, mailContent, record }) =>
  async (dispatch) => {
    let sendToSuccessorUrl = '';
    dispatch(setFetching(true));

    if (record?.document_type === 'Employee Appraisal Request') {
      sendToSuccessorUrl = 'send-to-line';
    } else if (record?.document_type === 'Job Handover Request') {
      sendToSuccessorUrl = 'send-to-successor';
    }

    let urlRequest = `${url}/${sendToSuccessorUrl}?`;
    urlRequest += `auth_token=${Service.userToken}`;
    urlRequest += '&source_type=portal';

    const data = JSON.stringify({
      unid,
      input_value: mailContent,
    });

    RequestApi(urlRequest, { data })
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          // dispatch(updateDocument(res.data.record));
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

export const submitCancelApproval =
  ({ objectId, unid }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `/approval-workflow-activity/submit-cancel-approval?`;
    urlRequest += `auth_token=${Service.userToken}`;
    urlRequest += '&source_type=portal';

    const data = JSON.stringify({
      unid,
      object_id: objectId,
      status: 'Pending Approval',
    });

    RequestApi(urlRequest, { data })
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          dispatch(updateDocument(res.data.record));
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

export const submitOpenApproval =
  ({ objectId, unid }) =>
  async (dispatch) => {
    dispatch(setFetching(true));

    let urlRequest = `/approval-workflow-activity/update-status?`;
    urlRequest += `auth_token=${Service.userToken}`;
    urlRequest += `&source_type=portal`;

    const data = JSON.stringify({
      unid,
      object_id: objectId,
      status: 'Open',
    });

    RequestApi(urlRequest, { data })
      .then((res) => {
        dispatch(setFetching(false));
        if (res.data.status === 'success') {
          dispatch(updateDocument(res.data.record));
        } else {
          NotificationMessage({ message: res.data.msg, status: res.data.status });
        }
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.log(err);
        NotificationMessage({ message: err.toString(), status: 'error' });
      });
  };

const dynamicPageReducer = dynamicPageSlice.reducer;

export default dynamicPageReducer;
